import type { UseQueryResult } from "@tanstack/react-query";
import { useScopeCloses } from "@api/close.api";
import { useOrganizationCloseStages } from "@api/organizationCloseStage.api";
import { useScope } from "@state/scopeContext.lib";
import type { MonthEndCloseViewParams } from "./monthEndCloseView";

const isAwaitingFirstFetch = ({
    isLoading,
    failureCount,
}: Pick<UseQueryResult, "isLoading" | "failureCount">) =>
    isLoading && failureCount === 0;

interface MonthEndCloseViewState extends MonthEndCloseViewParams {
    /** Closes or close stages are loading, retries included. */
    isLoading: boolean;
    /**
     * Closes or close stages are on their first fetch. Retries keep
     * `isLoading` on until they run out, so this counts a failed first fetch
     * as answered for callers that shouldn't wait out the retries.
     */
    isAwaitingFirstFetch: boolean;
}

/** The scope's inputs to `isMonthEndCloseViewShown`, shared so the workspace views and the page landing agree on them. */
export function useMonthEndCloseViewParams(): MonthEndCloseViewState {
    const { organizationId } = useScope();
    const closesQuery = useScopeCloses();
    const closeStagesQuery = useOrganizationCloseStages();

    return {
        organizationId,
        hasCloseStages: (closeStagesQuery.data ?? []).length > 0,
        hasCloses: (closesQuery.data ?? []).length > 0,
        isLoading: closesQuery.isLoading || closeStagesQuery.isLoading,
        isAwaitingFirstFetch:
            isAwaitingFirstFetch(closesQuery) ||
            isAwaitingFirstFetch(closeStagesQuery),
    };
}
